import { Link } from "react-router-dom";
import { MonoLabel, Em, Card } from "@/site/components/Atoms";
import PipelineV from "@/site/anim/PipelineV";

export default function Approach() {
  return (
    <>
      {/* HERO */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20">
        <div className="fade-up">
          <MonoLabel>APPROACH</MonoLabel>
        </div>
        <h1 className="h-display fade-up delay-1 mt-6 text-[44px] sm:text-[58px] lg:text-[72px] max-w-5xl">
          From opportunity insight <br className="hidden md:block" />
          to <Em>operational execution.</Em>
        </h1>
        <p className="fade-up delay-2 mt-7 max-w-2xl text-[15px] leading-[1.85] text-white/70">
          Forealco is built for companies that need more than a market report, a company formation
          service, or a local introduction. It provides a disciplined way to assess commercially
          relevant opportunities, shape the right route, align execution layers, and move toward
          practical market entry.
        </p>
        <div className="fade-up delay-3 mt-10 flex items-center gap-3 flex-wrap">
          <Link
            to="/explore-opportunities"
            data-testid="approach-hero-cta-primary"
            className="btn-primary"
          >
            Explore Opportunities →
          </Link>
          <Link to="/markets" data-testid="approach-hero-cta-secondary" className="btn-secondary">
            See Markets
          </Link>
        </div>
      </section>

      <Principle />

      <Stages />

      <Closing />
    </>
  );
}

/* ───────────────── Principle ───────────────── */
function Principle() {
  const questions = [
    "What is the real commercial purpose?",
    "Which market route is credible?",
    "Which entity, partner, or representation model is needed?",
    "How will banking, accounting, documentation, contracts, and reporting work?",
    "Which local, digital, advisory, trade, or institutional layers must be aligned?",
    "What makes the opportunity executable rather than only attractive?",
    "What must be clarified before time, capital, and reputation are committed?",
  ];

  return (
    <section
      data-testid="approach-principle"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28 lg:mt-32"
    >
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-4">
          <MonoLabel>OUR APPROACH</MonoLabel>
          <h2 className="h-display mt-6 text-[34px] md:text-[44px] lg:text-[48px] leading-[1.1]">
            Market interest must be tested <Em>before it becomes a route.</Em>
          </h2>
        </div>

        <div className="col-span-12 lg:col-span-8 lg:pl-6 space-y-6 text-[15px] leading-[1.95] text-white/75 max-w-3xl">
          <p>
            Forealco does not treat opportunity as a market signal alone. A market may look
            attractive. A tax position may look efficient. A trade route may look promising. An
            institutional program may appear open. But none of these elements create value unless
            they can be connected to a workable operating model.
          </p>
          <p>
            Our approach is to test the opportunity against structure, execution capacity, local
            conditions, institutional context, commercial logic, partner availability, and
            operational reality.
          </p>
          <p>We look at the practical questions behind the idea:</p>

          <ul className="space-y-2.5 pl-0 list-none pt-1">
            {questions.map((q, i) => (
              <li
                key={i}
                className="relative pl-6 text-[14.5px] leading-[1.75] text-white/75"
              >
                <span
                  className="absolute left-0 top-[0.7em] w-2.5 h-px bg-[#9DB4FF]/70"
                  aria-hidden="true"
                />
                {q}
              </li>
            ))}
          </ul>

          <p>
            Forealco's role is to move from general market interest to structured direction. The
            aim is not to produce a static report, but to help shape a route that can be
            understood, tested, coordinated, and gradually activated.
          </p>
          <p>
            This approach reflects a simple principle: market access is not created by information
            alone. It is created by the right structure, the right partners, the right operating
            route, and the discipline to test whether the opportunity can work in practice.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ───────────────── Path / Stages ───────────────── */
function Stages() {
  const stages = [
    {
      n: "01",
      t: "Discover",
      paragraphs: [
        "We examine the opportunity behind the signal. This includes market conditions, tax and trade context, institutional environment, commercial logic, partner availability, execution constraints, and the practical reasons why the opportunity may or may not work.",
        "This stage is not about confirming that a market is attractive. It is about understanding whether the opportunity has a credible commercial basis, a realistic route, and a practical reason to move forward.",
      ],
      bullets: ["Opportunity signal", "Market & institutional context", "Commercial logic"],
    },
    {
      n: "02",
      t: "Structure",
      paragraphs: [
        "We shape the route. This may include the entry model, company or representation structure, banking and accounting needs, partner roles, commercial path, documentation flow, and operational readiness.",
        "A structure is not only a legal setup. It is the way the business will function, communicate, contract, report, deliver, and remain credible in the target market.",
        "This stage helps define whether the opportunity should move through local presence, representation, distribution, advisory coordination, digital infrastructure, institutional access, trade coordination, or a combination of these layers.",
      ],
      bullets: ["Entry model", "Partner roles", "Operational readiness"],
    },
    {
      n: "03",
      t: "Coordinate",
      paragraphs: [
        "We coordinate the required layers around the route. Depending on the opportunity, this may involve advisory partners, local presence, accounting and legal support, digital infrastructure, institutional access, trade coordination, documentation, or operational support.",
        "The value of a network is not the number of contacts it contains. The value is whether the right actors can be aligned around a specific execution path.",
        "Forealco treats coordination as a practical discipline: who does what, where the responsibility sits, how the route is supported, and which gaps must be closed before execution begins.",
      ],
      bullets: ["Advisory & local presence", "Digital & operational layer", "Institutional access"],
    },
    {
      n: "04",
      t: "Realize",
      paragraphs: [
        "We support the transition from structured plan to practical activation. The objective is to help the opportunity become a commercially realistic and operationally grounded route.",
        "This does not mean every opportunity must be forced into execution. Sometimes the right outcome is to proceed, sometimes to redesign the route, and sometimes to wait. The discipline is knowing the difference before time, capital, and reputation are committed.",
        "Realization means bringing the structure closer to the market: clarifying next steps, aligning support layers, preparing engagement points, and helping the opportunity move from analysis toward controlled execution.",
      ],
      bullets: ["Activation", "Follow-through", "Operational grounding"],
    },
  ];

  return (
    <section
      data-testid="approach-path"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32"
    >
      {/* Section intro */}
      <div className="grid grid-cols-12 gap-10 mb-16">
        <div className="col-span-12 lg:col-span-4">
          <MonoLabel>THE PATH</MonoLabel>
          <h2 className="h-display mt-6 text-[34px] md:text-[44px] lg:text-[48px] leading-[1.1]">
            From signal to structure. <br className="hidden md:block" />
            <Em>From structure to execution.</Em>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-8 lg:pl-6">
          <p className="max-w-2xl text-[15px] leading-[1.95] text-white/70">
            Forealco works through a disciplined path designed to connect opportunity analysis with
            practical market movement. Each stage reduces ambiguity and brings the opportunity
            closer to an executable route.
          </p>
        </div>
      </div>

      {/* Stages */}
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3">
              ↓ THE PATH
            </div>
            <PipelineV height={760} />
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#9DB4FF] mt-3">
              EXECUTION
            </div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-9 space-y-14">
          {stages.map((s) => (
            <div
              key={s.n}
              data-testid={`approach-stage-${s.n}`}
              className="border-t border-white/10 pt-10"
            >
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-2">
                  <div className="font-display italic text-[#9DB4FF] text-[36px] leading-none">
                    {s.n}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-10">
                  <div className="font-display text-[34px] md:text-[42px] leading-[1.05]">
                    {s.t}
                  </div>
                  <div className="mt-5 max-w-2xl space-y-4 text-[15px] leading-[1.9] text-white/72">
                    {s.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                    {s.bullets.map((b) => (
                      <Card key={b} className="px-4 py-3">
                        <MonoLabel>FOCUS</MonoLabel>
                        <div className="mt-2 text-[13px] text-white/80">{b}</div>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────── Closing ───────────────── */
function Closing() {
  return (
    <section
      data-testid="approach-closing"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32"
    >
      <div className="border border-white/10 bg-white/[0.025] p-10 md:p-16 relative overflow-hidden">
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, #9DB4FF 50%, transparent)",
            opacity: 0.5,
          }}
          aria-hidden="true"
        />
        <MonoLabel>CLOSING</MonoLabel>
        <h2 className="h-display mt-6 text-[30px] md:text-[42px] lg:text-[48px] leading-[1.12] max-w-4xl">
          A disciplined path from market interest <br className="hidden md:block" />
          to <Em>executable structure.</Em>
        </h2>

        <div className="mt-8 space-y-5 max-w-3xl text-[15px] leading-[1.9] text-white/72">
          <p>
            Forealco is designed for companies that need market access, local structure, partner
            coordination, and execution clarity. The work begins with opportunity insight, but it
            does not end there.
          </p>
          <p>
            A viable opportunity must survive contact with practical questions: who will operate,
            who will support, who will document, who will represent, who will finance, who will
            deliver, and how the structure will remain credible over time.
          </p>
          <p>
            Forealco helps bring these questions into one disciplined path, so that market interest
            can move toward operational execution with greater clarity and control.
          </p>
          <p>
            The objective is not to make every opportunity look possible. The objective is to
            identify which opportunities can be structured, which require further preparation, and
            which should not move forward until the route becomes clearer.
          </p>
        </div>

        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <Link
            to="/explore-opportunities"
            data-testid="approach-closing-cta-primary"
            className="btn-primary"
          >
            Explore Opportunities →
          </Link>
          <Link
            to="/markets"
            data-testid="approach-closing-cta-secondary"
            className="btn-secondary"
          >
            See Markets
          </Link>
        </div>
      </div>
    </section>
  );
}
